import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ApiContext } from '@api/api';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import { SnRouteConsolidationGroupOutDTO, SnRouteConsolidationOutDTO, StopTypeEnum, SupplierNotificationTypeEnum } from '@api/ringil-api.v2.tsx';
import COHeadline from '@components/framework/headlines/COHeadline.tsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton';
import CloseButton from '@components/RingilComponents/CloseButton.tsx';
import RingilRoutesMap, { MapRoute } from '@components/RingilComponents/RoutesMap/RingilRoutesMap.tsx';
import { CATEGORICAL_COLORS } from '@styles/categoricalPalette.ts';
import { clickableTextClass } from '@components/RingilComponents/clickableText.ts';
import SecuredArea from '@components/security/SecuredArea';
import { SecuredFlag } from '@components/security/types.ts';
import SvgRoute from '@icons/route.svg?react';
import SvgChevron from '@icons/chevron.svg?react';
import SvgDelete from '@icons/delete-trash.svg?react';
import SvgEye from '@icons/eye.svg?react';
import SvgEyeSlash from '@icons/eye-slash.svg?react';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import useNavigation from '@hooks/useNavigation.tsx';
import { useLovTruckTypes } from '@hooks/customer.hooks.tsx';
import { formatNumber } from '@utils/utils.tsx';
import {
  loadGroupEdits,
  loadHiddenGroups,
  saveGroupEdits,
  saveHiddenGroups,
} from '@app/pages/customer/supplier-notification/route-consolidation/routeConsolidationStorage.ts';
import {
  createDisabledReason,
  editedGroupOf,
  estimatedUtilisationOf,
  removedNotificationsOf,
  SnLoad,
  snLoadOf,
  stopStateOf,
  CreatedFromGroup,
  groupRoutesOf,
  overviewOf,
  percentOf,
  reasonLabel,
  statusLabel,
} from '@app/pages/customer/supplier-notification/route-consolidation/routeConsolidationResult.ts';

interface ComponentProps {
  result: SnRouteConsolidationOutDTO;
  /** Groups already turned into shipments in this session, by group id. */
  created: Record<number, CreatedFromGroup>;
  /** Merging notifications into a shipment is a feature of its own; without it the Create button is hidden, as the merge action is. */
  mergeFlag: SecuredFlag;
  /** The group as edited (locations removed), and the notifications that were taken out of it. */
  onCreate: (group: SnRouteConsolidationGroupOutDTO, removedSupplierNotificationIds: string[]) => void;
  /** Runs the optimization again for the same notifications, e.g. after fixing the data behind "Not optimized". */
  onRecalculate: () => void;
  recalculating: boolean;
  onClose: () => void;
}

/** More than this many unoptimized notifications and the list starts collapsed. */
const NOT_OPTIMIZED_OPEN_LIMIT = 5;

const km = (value: number) => `${formatNumber(value ?? 0, 0)} km`;

interface CollapsibleProps {
  title: string;
  initiallyOpen?: boolean;
  /** Controlled use: pass both to open it from outside. */
  open?: boolean;
  onToggle?: (open: boolean) => void;
  /**
   * Once opened, keep the children mounted and only hide them when folded: for content that is costly
   * to set up again, or that must not be torn down mid-load (the map).
   */
  keepMounted?: boolean;
  dataTestId?: string;
  children: React.ReactNode;
}

/** Children mount only once opened, so whatever they load (the map's scripts) waits until then. */
const Collapsible: React.FC<CollapsibleProps> = ({ title, initiallyOpen = false, open: controlledOpen, onToggle, keepMounted = false, dataTestId, children }) => {
  const [ownOpen, setOwnOpen] = useState(initiallyOpen);
  const open = controlledOpen ?? ownOpen;
  const [everOpened, setEverOpened] = useState(open);
  useEffect(() => {
    if (open) setEverOpened(true);
  }, [open]);
  return (
    <div className={'flex flex-col gap-2'} data-testid={dataTestId}>
      <RingilButton
        mode={'text-only'}
        label={title}
        icon={<SvgChevron className={clsx('h-4 w-4 transition-transform', open && 'rotate-180')} />}
        onClick={() => (onToggle ? onToggle(!open) : setOwnOpen(!open))}
        className={'self-start'}
        dataTestId={dataTestId ? `${dataTestId}-toggle` : undefined}
      />
      {keepMounted ? everOpened && <div className={clsx(!open && 'hidden')}>{children}</div> : open && children}
    </div>
  );
};

/** A route's colour as a mark, for beside its letter. */
const RouteSwatch: React.FC<{ color?: string }> = ({ color }) =>
  color ? <span className={'inline-block h-1.5 w-5 shrink-0 rounded-full ring-1 ring-white'} style={{ backgroundColor: color }} /> : null;

/** `estimated` marks a figure worked out here rather than sent by the optimizer. */
const UtilisationBar: React.FC<{ label: string; fraction: number; estimated?: boolean }> = ({ label, fraction, estimated = false }) => {
  const percent = percentOf(fraction);
  return (
    <div className={'flex flex-col gap-1'}>
      <div className={'flex flex-row justify-between text-sm text-content-secondary'}>
        <span>{label}</span>
        <span>
          {estimated && '≈ '}
          {percent} %
        </span>
      </div>
      <div className={'h-2 w-full overflow-hidden rounded bg-base-tertiary'}>
        <div className={'h-full rounded bg-content-brand'} style={{ width: `${percent}%` }} />
      </div>
    </div>
  );
};

/**
 * What a route optimization came up with, shown above the notification list.
 *
 * An information box rather than a modal: the list stays usable underneath, and the box survives the
 * trip to the create form and back, so the next group can be created from it right away.
 */
const RouteConsolidationPanel: React.FC<ComponentProps> = ({ result, created, mergeFlag, onCreate, onRecalculate, recalculating, onClose }) => {
  const { tr } = useTranslationLgs();
  const navigation = useNavigation();
  const truckTypes = useLovTruckTypes();
  const { cuGetMultipleSupplierNotificationsForExpedition } = useContext(ApiContext);
  const overview = useMemo(() => overviewOf(result), [result]);

  // Locations the user removed, per group; kept in the tab with the result so they survive the trip to
  // the create form and back. Everything else is derived from the untouched groups, so adding a location
  // back restores exactly the state before it was removed.
  const [edits, setEdits] = useState<Record<number, number[]>>(() => loadGroupEdits(result.consolidationRequestId));
  useEffect(() => setEdits(loadGroupEdits(result.consolidationRequestId)), [result.consolidationRequestId]);
  const setRemovedStops = (groupId: number, removedStops: number[]) => {
    const next = { ...edits, [groupId]: removedStops };
    setEdits(next);
    saveGroupEdits(result.consolidationRequestId, next);
  };
  const removedStopsOf = (groupId: number) => edits[groupId] ?? [];
  const editedGroups = useMemo(() => new Map(overview.groups.map(group => [group.groupId, editedGroupOf(group, edits[group.groupId] ?? [])])), [overview.groups, edits]);

  // Groups taken off the list (and the map) — kept with the result like the location edits — and groups
  // folded to their header, which is only a view preference and not kept.
  const [hidden, setHidden] = useState<number[]>(() => loadHiddenGroups(result.consolidationRequestId));
  useEffect(() => setHidden(loadHiddenGroups(result.consolidationRequestId)), [result.consolidationRequestId]);
  const setGroupHidden = (groupId: number, hide: boolean) => {
    const next = hide ? [...hidden, groupId] : hidden.filter(id => id !== groupId);
    setHidden(next);
    saveHiddenGroups(result.consolidationRequestId, next);
    if (hide) setHighlightedGroupId(current => (current === groupId ? null : current));
  };
  const [folded, setFolded] = useState<number[]>([]);
  const toggleFolded = (groupId: number) => setFolded(current => (current.includes(groupId) ? current.filter(id => id !== groupId) : [...current, groupId]));

  // What each notification of an edited group puts on the truck, for estimating its utilisation. Loaded
  // only once a group is edited, a group at a time (the endpoint takes at most ten), and never twice.
  const [loads, setLoads] = useState<Map<string, SnLoad>>(new Map());
  const requestedRef = useRef(new Set<string>());
  useEffect(() => {
    overview.groups
      .filter(group => (edits[group.groupId] ?? []).length > 0)
      .map(group => group.supplierNotificationIds.filter(id => !requestedRef.current.has(id)))
      .filter(ids => ids.length > 0)
      .forEach(ids => {
        ids.forEach(id => requestedRef.current.add(id));
        for (let index = 0; index < ids.length; index += 10) {
          cuGetMultipleSupplierNotificationsForExpedition(
            ids.slice(index, index + 10),
            notifications =>
              setLoads(current => {
                const next = new Map(current);
                (notifications ?? []).forEach(notification => next.set(notification.supplierNotificationFromId, snLoadOf(notification)));
                return next;
              }),
            null,
            // Without the details the optimizer's figures stay, labelled as being for the full group.
            { onError: () => {}, onValidationFailed: () => {} },
          );
        }
      });
  }, [overview.groups, edits]);

  // Letters and colours by the groups' order, routes as edited.
  const routes = useMemo(
    () =>
      groupRoutesOf(
        overview.groups.map(group => editedGroups.get(group.groupId)),
        CATEGORICAL_COLORS,
      ),
    [overview.groups, editedGroups],
  );

  // One route picked out on the map; picking it again, or the map's background, shows all alike again.
  const [mapOpen, setMapOpen] = useState(false);
  const [highlightedGroupId, setHighlightedGroupId] = useState<number | null>(null);
  // Card letters and legend entries toggle their own route, and pick another one straight away.
  const toggleHighlight = (groupId: number) => setHighlightedGroupId(current => (current === groupId ? null : groupId));
  // On the map routes overlap on shared roads, so a click meant for the highlighted line often lands on a
  // faded one underneath. While a route is highlighted, any click on the map therefore just clears it.
  const highlightFromMap = (groupId: number | null) => setHighlightedGroupId(current => (current !== null ? null : groupId));
  const routeOf = (group: SnRouteConsolidationGroupOutDTO) => routes.find(route => route.groupId === group.groupId);
  // Drawn: coloured routes with something to pin. Memoised for the same reason.
  // Groups taken off the list leave the map too, but keep their letter and colour so the others are not repainted.
  const drawnRoutes = useMemo(() => routes.filter(route => route.color && route.stops.length > 0 && !hidden.includes(route.groupId)), [routes, hidden]);
  const mapRoutes = useMemo<MapRoute[]>(() => drawnRoutes.map(route => ({ id: route.groupId, color: route.color, stops: route.stops })), [drawnRoutes]);

  const truckTypeName = (code: string) => truckTypes.find(type => type.code === code)?.name ?? code;
  const applicationIdOf = (snId: string) => result.supplierNotifications.find(record => record.supplierNotificationId === snId)?.applicationId ?? snId;

  const snLink = (snId: string, applicationId?: string) => (
    <Link
      key={snId}
      className={clsx('no-underline', clickableTextClass)}
      to={navigation.createNavigationLink(navigation.urlFunctions.createSupplierNotificationDetail(snId, SupplierNotificationTypeEnum.EFXX))}
    >
      {applicationId || applicationIdOf(snId)}
    </Link>
  );

  const renderGroup = (group: SnRouteConsolidationGroupOutDTO) => {
    const createdFrom = created[group.groupId];
    const removedStops = removedStopsOf(group.groupId);
    const removedNotifications = removedNotificationsOf(group, removedStops);
    const edited = editedGroups.get(group.groupId);
    const disabledReason = createDisabledReason(edited, createdFrom, tr);
    // A group already turned into a shipment is a record of what was created; it is no longer edited.
    const editable = !createdFrom;
    const estimate = estimatedUtilisationOf(group, edited, loads);
    const isFolded = folded.includes(group.groupId);

    return (
      <div
        key={group.groupId}
        className={'flex flex-col gap-4 rounded-2xl border-2 border-solid border-transparent bg-base-base p-4'}
        // The highlighted group's card is framed in its route's colour, matching the map.
        style={highlightedGroupId === group.groupId && routeOf(group)?.color ? { borderColor: routeOf(group).color } : undefined}
        data-testid={`routeConsolidationGroup-${group.groupId}`}
      >
        <div className={'flex flex-row flex-wrap items-start justify-between gap-4'}>
          <div className={'flex flex-col gap-1'}>
            <span className={'flex flex-row items-center gap-3'}>
              {routeOf(group)?.color && routeOf(group).stops.length > 0 ? (
                <RingilButton
                  mode={'text-only'}
                  icon={<RouteSwatch color={routeOf(group).color} />}
                  label={<strong className={'text-content-primary'}>{routeOf(group).letter}</strong>}
                  tooltip={
                    highlightedGroupId === group.groupId
                      ? tr('RouteConsolidation.showAllRoutes', 'Show all routes alike again')
                      : tr('RouteConsolidation.highlightOnMap', 'Highlight this route on the map')
                  }
                  onClick={() => {
                    // Highlighting opens the map, so the route can be seen; un-highlighting leaves it as it is.
                    if (highlightedGroupId !== group.groupId) setMapOpen(true);
                    toggleHighlight(group.groupId);
                  }}
                  dataTestId={`routeConsolidationGroup-${group.groupId}-highlight`}
                />
              ) : (
                <strong>{routeOf(group)?.letter}</strong>
              )}
              <strong>{truckTypeName(group.truckTypeCode)}</strong>
            </span>
            <span className={'text-sm text-content-secondary'}>
              {tr('RouteConsolidation.groupKm', '{{before}} → {{after}}, saves {{savings}}', {
                before: km(group.kmBefore),
                after: km(group.kmAfter),
                savings: km(group.kmSavings),
              })}
            </span>
          </div>

          <div className={'flex flex-col items-end gap-1'}>
            <div className={'flex flex-row items-center gap-1'}>
              <RingilButton
                mode={'text-only'}
                icon={<SvgChevron className={clsx('h-4 w-4 transition-transform', !isFolded && 'rotate-180')} />}
                tooltip={isFolded ? tr('RouteConsolidation.unfoldGroup', 'Show the group details') : tr('RouteConsolidation.foldGroup', 'Fold the group')}
                onClick={() => toggleFolded(group.groupId)}
                dataTestId={`routeConsolidationGroup-${group.groupId}-fold`}
              />
              <RingilButton
                mode={'text-only'}
                // The SVG paints its paths in a fixed colour; take the button's instead, as the table's hidden views do.
                icon={<SvgEyeSlash className={'h-4 w-4 [&_path]:fill-current'} />}
                tooltip={tr('RouteConsolidation.hideGroup', 'Remove the group from the list and the map; it can be shown again below')}
                onClick={() => setGroupHidden(group.groupId, true)}
                dataTestId={`routeConsolidationGroup-${group.groupId}-hide`}
              />
            </div>
            {createdFrom ? (
              <span className={'text-sm'} data-testid={`routeConsolidationGroup-${group.groupId}-created`}>
                {tr('RouteConsolidation.groupCreated', 'Created')} →{' '}
                <Link
                  className={clsx('no-underline', clickableTextClass)}
                  to={navigation.createNavigationLink(navigation.urlFunctions.createExpeditionDetailV2(createdFrom.expeditionId))}
                >
                  {createdFrom.applicationId}
                </Link>
              </span>
            ) : (
              <SecuredArea id={'route-consolidation-create'} instance={String(group.groupId)} visible={mergeFlag} label={'Create expedition from optimized group'}>
                <RingilButton
                  mode={'filled-rectangle'}
                  label={tr('RouteConsolidation.createExpedition', 'Create expedition')}
                  disabled={disabledReason !== undefined}
                  onClick={() => onCreate(edited, Array.from(removedNotifications))}
                  dataTestId={`routeConsolidationGroup-${group.groupId}-create`}
                />
              </SecuredArea>
            )}
            {/* Spelled out rather than in a tooltip: a disabled button does not reliably report hovering. */}
            {!createdFrom && disabledReason && <span className={'max-w-[320px] text-right text-sm text-content-secondary'}>{disabledReason}</span>}
          </div>
        </div>

        {!isFolded && (
          <>
            <div className={'grid grid-cols-1 gap-3 md:grid-cols-2'}>
              <UtilisationBar
                label={tr('RouteConsolidation.floorUtilisation', 'Floor utilisation')}
                fraction={estimate?.floorUtilisation ?? group.floorUtilisation}
                estimated={estimate?.floorUtilisation !== undefined}
              />
              <UtilisationBar
                label={tr('RouteConsolidation.weightUtilisation', 'Weight utilisation')}
                fraction={estimate?.weightUtilisation ?? group.weightUtilisation}
                estimated={estimate?.weightUtilisation !== undefined}
              />
            </div>

            {removedStops.length > 0 && (
              <div
                className={'flex flex-row flex-wrap items-center gap-x-4 gap-y-1 text-sm text-content-secondary'}
                data-testid={`routeConsolidationGroup-${group.groupId}-edited`}
              >
                <span>
                  {tr(
                    'RouteConsolidation.groupEdited',
                    estimate
                      ? "{{count}} supplier notifications removed. Utilisation (≈) is estimated from the remaining notifications' weight and floor area; kilometres are the optimizer's figures for the full group."
                      : "{{count}} supplier notifications removed. Kilometres and utilisation are the optimizer's figures for the full group.",
                    { count: removedNotifications.size },
                  )}
                </span>
                {editable && (
                  <RingilButton
                    mode={'text-only'}
                    label={tr('RouteConsolidation.restoreAll', 'Add all back')}
                    onClick={() => setRemovedStops(group.groupId, [])}
                    dataTestId={`routeConsolidationGroup-${group.groupId}-restoreAll`}
                  />
                )}
              </div>
            )}

            <ol className={'m-0 flex list-none flex-col gap-2 p-0'}>
              {[...group.stops]
                .sort((a, b) => a.loadingOrder - b.loadingOrder)
                .map(stop => {
                  const state = stopStateOf(stop, removedStops, removedNotifications);
                  return (
                    <li
                      key={stop.loadingOrder}
                      className={'flex flex-row items-start gap-3'}
                      data-testid={`routeConsolidationGroup-${group.groupId}-stop-${stop.loadingOrder}`}
                    >
                      <span className={'w-6 shrink-0 text-right text-content-secondary'}>{stop.loadingOrder}.</span>
                      <div className={clsx('flex flex-col gap-1', state !== 'kept' && 'opacity-50')}>
                        <span className={clsx(state !== 'kept' && 'line-through')}>{stop.entries.find(entry => entry.locationName)?.locationName ?? '—'}</span>
                        <div className={'flex flex-row flex-wrap gap-2'}>
                          {stop.entries.map(entry => (
                            <span
                              key={`${entry.supplierNotificationId}-${entry.stopType}`}
                              className={clsx(
                                'inline-flex items-center gap-1 rounded bg-base-tertiary px-2 py-0.5 text-sm',
                                removedNotifications.has(entry.supplierNotificationId) && 'line-through opacity-60',
                              )}
                            >
                              <span className={'text-content-secondary'}>
                                {entry.stopType === StopTypeEnum.LOAD ? tr('RouteConsolidation.stopLoad', 'Load') : tr('RouteConsolidation.stopUnload', 'Unload')}
                              </span>
                              {snLink(entry.supplierNotificationId, entry.applicationId)}
                            </span>
                          ))}
                        </div>
                        {state === 'emptied' && (
                          <span className={'text-sm text-content-secondary'}>
                            {tr('RouteConsolidation.stopEmptied', 'Dropped: all its supplier notifications were removed with another location.')}
                          </span>
                        )}
                      </div>
                      {editable && state === 'kept' && (
                        <RingilButton
                          mode={'table'}
                          size={'small'}
                          icon={<SvgDelete />}
                          tooltip={tr('RouteConsolidation.removeStop', 'Remove this location and every supplier notification handled here')}
                          onClick={() => setRemovedStops(group.groupId, [...removedStops, stop.loadingOrder])}
                          className={'ml-auto shrink-0'}
                          dataTestId={`routeConsolidationGroup-${group.groupId}-stop-${stop.loadingOrder}-remove`}
                        />
                      )}
                      {editable && state === 'removed' && (
                        <RingilButton
                          mode={'text-only'}
                          label={tr('RouteConsolidation.addStopBack', 'Add back')}
                          onClick={() =>
                            setRemovedStops(
                              group.groupId,
                              removedStops.filter(order => order !== stop.loadingOrder),
                            )
                          }
                          className={'ml-auto shrink-0'}
                          dataTestId={`routeConsolidationGroup-${group.groupId}-stop-${stop.loadingOrder}-restore`}
                        />
                      )}
                    </li>
                  );
                })}
            </ol>
          </>
        )}
      </div>
    );
  };

  return (
    <div className={'mb-4 flex flex-col gap-4 rounded-2xl bg-base-secondary px-4 py-6'} data-testid={'routeConsolidationPanel'}>
      <div className={'flex flex-row items-start justify-between gap-4'}>
        <div className={'flex flex-col gap-1'}>
          <COHeadline variant={'h3'} title={tr('RouteConsolidation.heading', 'Route optimization')} icon={<SvgRoute />} />
          <span className={'text-content-secondary'} data-testid={'routeConsolidationCounts'}>
            {tr(
              'RouteConsolidation.counts',
              '{{groups}} groups · {{inGroups}} supplier notifications in groups · {{notOptimized}} not optimized · saves {{savings}} in total',
              {
                groups: overview.groups.length,
                inGroups: overview.notificationsInGroups,
                notOptimized: overview.notOptimized.length,
                savings: km(overview.totalKmSavings),
              },
            )}
          </span>
        </div>
        <div className={'flex flex-row items-center gap-4'}>
          <RingilButton
            mode={'outlined'}
            label={tr('RouteConsolidation.recalculate', 'Recalculate')}
            loading={recalculating}
            disabled={recalculating}
            onClick={onRecalculate}
            dataTestId={'routeConsolidationRecalculate'}
          />
          <CloseButton onClick={onClose} dataTestId={'routeConsolidationClose'} />
        </div>
      </div>

      {overview.groups.length > 0 ? (
        <>
          <div className={'flex flex-col gap-3'}>{overview.groups.filter(group => !hidden.includes(group.groupId)).map(renderGroup)}</div>

          {/* Folded until asked for: HERE's scripts, tiles and the road routing load only then. */}
          {drawnRoutes.length > 0 && (
            <Collapsible title={tr('RouteConsolidation.showMap', 'Map')} open={mapOpen} onToggle={setMapOpen} keepMounted dataTestId={'routeConsolidationMap'}>
              <div className={'flex flex-col gap-2'}>
                <div className={'flex flex-row flex-wrap items-center gap-2'} data-testid={'routeConsolidationMapLegend'}>
                  {drawnRoutes.map(route => {
                    const group = overview.groups.find(candidate => candidate.groupId === route.groupId);
                    // Selected = filled, the rest outlined, as every toggle in the app.
                    return (
                      <RingilButton
                        key={route.groupId}
                        mode={highlightedGroupId === route.groupId ? 'filled-rectangle' : 'outlined-rectangle'}
                        icon={<RouteSwatch color={route.color} />}
                        label={`${route.letter} · ${truckTypeName(group.truckTypeCode)} · ${km(group.kmSavings)}`}
                        onClick={() => toggleHighlight(route.groupId)}
                        dataTestId={`routeConsolidationMapLegend-${route.groupId}`}
                      />
                    );
                  })}
                </div>
                {routes.length > CATEGORICAL_COLORS.length && (
                  <span className={'text-sm text-content-secondary'}>
                    {tr('RouteConsolidation.mapLimited', 'The map shows the {{count}} groups with the biggest saving.', { count: CATEGORICAL_COLORS.length })}
                  </span>
                )}
                <RingilRoutesMap
                  routes={mapRoutes}
                  highlightedId={highlightedGroupId}
                  onRouteClick={id => highlightFromMap(id as number | null)}
                  className={'h-[320px] w-full lg:h-[520px]'}
                />
              </div>
            </Collapsible>
          )}
        </>
      ) : (
        <span>{tr('RouteConsolidation.noGroups', 'The optimization found no supplier notifications that could share a truck.')}</span>
      )}

      {overview.soloGroups.length > 0 && (
        <Collapsible
          title={tr('RouteConsolidation.noMergeFound', 'No merge found ({{count}})', { count: overview.soloGroups.length })}
          dataTestId={'routeConsolidationSolo'}
        >
          <div className={'flex flex-row flex-wrap gap-3 pl-6'}>{overview.soloGroups.flatMap(group => group.supplierNotificationIds.map(snId => snLink(snId)))}</div>
        </Collapsible>
      )}

      {overview.notOptimized.length > 0 && (
        <Collapsible
          title={tr('RouteConsolidation.notOptimized', 'Not optimized ({{count}})', { count: overview.notOptimized.length })}
          initiallyOpen={overview.notOptimized.length <= NOT_OPTIMIZED_OPEN_LIMIT}
          dataTestId={'routeConsolidationNotOptimized'}
        >
          <ul className={'m-0 flex list-none flex-col gap-2 pl-6'}>
            {overview.notOptimized.map(record => (
              <li key={record.supplierNotificationId} className={'flex flex-row flex-wrap gap-2'}>
                {snLink(record.supplierNotificationId, record.applicationId)}
                <span className={'text-content-secondary'}>
                  {statusLabel(record.consolidationStatus, tr)}
                  {record.consolidationReasons?.length > 0 && `: ${record.consolidationReasons.map(code => reasonLabel(code, tr)).join(', ')}`}
                </span>
              </li>
            ))}
          </ul>
        </Collapsible>
      )}

      {/* Last, after everything still in play. */}
      {hidden.length > 0 && (
        <Collapsible title={tr('RouteConsolidation.hiddenGroups', 'Removed from the list ({{count}})', { count: hidden.length })} dataTestId={'routeConsolidationHidden'}>
          <ul className={'m-0 flex list-none flex-col gap-2 pl-6'}>
            {overview.groups
              .filter(group => hidden.includes(group.groupId))
              .map(group => (
                <li key={group.groupId} className={'flex flex-row flex-wrap items-center gap-3'}>
                  <RouteSwatch color={routeOf(group)?.color} />
                  <strong>{routeOf(group)?.letter}</strong>
                  <span className={'text-content-secondary'}>
                    {truckTypeName(group.truckTypeCode)} · {km(group.kmSavings)}
                  </span>
                  <RingilButton
                    mode={'text-only'}
                    label={tr('RouteConsolidation.showGroupAgain', 'Show again')}
                    icon={<SvgEye className={'h-4 w-4 [&_path]:fill-current'} />}
                    onClick={() => setGroupHidden(group.groupId, false)}
                    dataTestId={`routeConsolidationGroup-${group.groupId}-unhide`}
                  />
                </li>
              ))}
          </ul>
        </Collapsible>
      )}
    </div>
  );
};

export default RouteConsolidationPanel;
